(function () {
  'use strict';

  document.documentElement.classList.add('tv8-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktop = window.matchMedia('(min-width: 1100px)');
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var runtimePromise;
  var revealObserver;
  var modelControllers = new Set();
  var marqueeControllers = new Set();
  var modelControllerByView = new WeakMap();
  var marqueeControllerByRoot = new WeakMap();
  var navigationControllerByDetails = new WeakMap();

  function onMediaChange(query, callback) {
    if (query.addEventListener) query.addEventListener('change', callback);
    else if (query.addListener) query.addListener(callback);
  }

  function saveDataEnabled() {
    return Boolean(connection && connection.saveData);
  }

  function waitForModelViewer() {
    if (window.customElements && window.customElements.get('model-viewer')) return Promise.resolve();
    if (!window.customElements || !window.customElements.whenDefined) {
      return Promise.reject(new Error('Custom elements are unavailable'));
    }

    return new Promise(function (resolve, reject) {
      var timeout = window.setTimeout(function () {
        reject(new Error('The 3D viewer did not initialize'));
      }, 12000);

      window.customElements.whenDefined('model-viewer').then(function () {
        window.clearTimeout(timeout);
        resolve();
      }, function (error) {
        window.clearTimeout(timeout);
        reject(error);
      });
    });
  }

  function loadRuntime(src) {
    if (window.customElements && window.customElements.get('model-viewer')) return Promise.resolve();
    if (!src) return Promise.reject(new Error('The 3D viewer runtime is missing'));
    if (runtimePromise) return runtimePromise;

    runtimePromise = new Promise(function (resolve, reject) {
      var script = document.querySelector('script[data-tv8-model-runtime]');

      function ready() {
        waitForModelViewer().then(resolve, reject);
      }

      function failed() {
        reject(new Error('The 3D viewer runtime could not be loaded'));
      }

      if (script) {
        if (script.dataset.tv8RuntimeState === 'ready') {
          ready();
          return;
        }
        if (script.dataset.tv8RuntimeState === 'failed') {
          script.remove();
          script = null;
        } else {
          script.addEventListener('load', ready, { once: true });
          script.addEventListener('error', failed, { once: true });
          return;
        }
      }

      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.tv8ModelRuntime = '';
      script.addEventListener('load', function () {
        script.dataset.tv8RuntimeState = 'ready';
        ready();
      }, { once: true });
      script.addEventListener('error', function () {
        script.dataset.tv8RuntimeState = 'failed';
        failed();
      }, { once: true });
      document.head.appendChild(script);
    }).catch(function (error) {
      runtimePromise = null;
      throw error;
    });

    return runtimePromise;
  }

  function initReveal(context) {
    var items = Array.from((context || document).querySelectorAll('[data-tv8-reveal]:not([data-tv8-reveal-ready])'));
    if (!items.length) return;

    items.forEach(function (item) {
      item.setAttribute('data-tv8-reveal-ready', 'true');
    });

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    items.forEach(function (item) {
      revealObserver.observe(item);
    });
  }

  function initProductView(view) {
    if (!view || view.dataset.tv8ModelReady === 'true') return;

    var stage = view.querySelector('[data-tv8-model-stage]');
    var model = view.querySelector('[data-tv8-model]');
    var poster = view.querySelector('[data-tv8-model-poster]');
    var loadButton = view.querySelector('[data-tv8-load-model]');
    var loadLabel = view.querySelector('[data-tv8-load-label]');
    var rotationButton = view.querySelector('[data-tv8-rotation-toggle]');
    var rotationLabel = view.querySelector('[data-tv8-rotation-label]');

    if (!stage || !model || !loadButton) return;
    view.dataset.tv8ModelReady = 'true';

    var loading = false;
    var loaded = false;
    var destroyed = false;
    var inView = true;
    var interactionPaused = false;
    var userPaused = reduceMotion.matches;
    var reducedMotionOverride = false;
    var restoreFocus = false;
    var nearViewport = false;
    var loadTimeout;
    var visibilityObserver;
    var autoLoadObserver;
    var autoLoadEnabled = view.hasAttribute('data-tv8-auto-model');

    function setLoadLabel(value) {
      if (loadLabel) loadLabel.textContent = value;
    }

    function setRotationLabel() {
      if (!rotationLabel) return;
      rotationLabel.textContent = userPaused ? 'Play rotation' : 'Pause rotation';
      if (rotationButton) rotationButton.setAttribute('aria-label', userPaused ? 'Play product rotation' : 'Pause product rotation');
    }

    function rotationAllowed() {
      var motionPreferenceAllows = !reduceMotion.matches || reducedMotionOverride;
      return loaded && !userPaused && !interactionPaused && motionPreferenceAllows && inView && !document.hidden;
    }

    function syncRotation() {
      if (!loaded) return;
      if (rotationAllowed()) model.setAttribute('auto-rotate', '');
      else model.removeAttribute('auto-rotate');
      setRotationLabel();
    }

    function removeLoadListeners() {
      model.removeEventListener('load', markLoaded);
      model.removeEventListener('error', markUnavailable);
      window.clearTimeout(loadTimeout);
    }

    function markLoaded() {
      if (loaded || destroyed) return;
      removeLoadListeners();
      loaded = true;
      loading = false;
      stage.classList.remove('is-unavailable');
      stage.classList.add('is-loaded');
      stage.removeAttribute('aria-busy');
      model.removeAttribute('aria-hidden');
      model.removeAttribute('tabindex');
      loadButton.disabled = false;
      loadButton.hidden = true;
      if (rotationButton) rotationButton.hidden = false;
      if (poster) {
        poster.alt = '';
        poster.setAttribute('aria-hidden', 'true');
      }
      setRotationLabel();
      syncRotation();
      if (restoreFocus) model.focus();
      restoreFocus = false;
    }

    function markUnavailable() {
      if (loaded || destroyed) return;
      removeLoadListeners();
      loading = false;
      stage.classList.remove('is-loaded');
      stage.classList.add('is-unavailable');
      stage.removeAttribute('aria-busy');
      model.removeAttribute('src');
      model.setAttribute('aria-hidden', 'true');
      model.setAttribute('tabindex', '-1');
      if (rotationButton) rotationButton.hidden = true;
      loadButton.hidden = false;
      loadButton.disabled = false;
      setLoadLabel('Retry 3D');
      if (restoreFocus) loadButton.focus();
      restoreFocus = false;
    }

    function activate(userInitiated) {
      if (loaded || loading || destroyed) return;
      if (!userInitiated && (reduceMotion.matches || saveDataEnabled())) return;

      restoreFocus = Boolean(userInitiated && document.activeElement === loadButton);
      loading = true;
      stage.classList.remove('is-unavailable');
      stage.setAttribute('aria-busy', 'true');
      loadButton.disabled = true;
      setLoadLabel('Loading 3D...');

      loadRuntime(model.dataset.runtimeSrc).then(function () {
        if (destroyed) return;
        model.addEventListener('load', markLoaded, { once: true });
        model.addEventListener('error', markUnavailable, { once: true });
        loadTimeout = window.setTimeout(markUnavailable, 30000);
        model.setAttribute('src', model.dataset.modelSrc);
        if (model.loaded) markLoaded();
      }).catch(function () {
        if (!destroyed) markUnavailable();
      });
    }

    loadButton.hidden = false;
    setRotationLabel();

    function handleLoadClick() {
      activate(true);
    }

    function handleRotationClick() {
      userPaused = !userPaused;
      reducedMotionOverride = reduceMotion.matches && !userPaused;
      syncRotation();
    }

    function pauseForInteraction() {
      interactionPaused = true;
      syncRotation();
    }

    function resumeAfterInteraction(event) {
      if (event && event.type === 'focusout' && stage.contains(event.relatedTarget)) return;
      interactionPaused = false;
      syncRotation();
    }

    loadButton.addEventListener('click', handleLoadClick);
    if (rotationButton) rotationButton.addEventListener('click', handleRotationClick);
    stage.addEventListener('pointerdown', pauseForInteraction);
    stage.addEventListener('pointerup', resumeAfterInteraction);
    stage.addEventListener('pointercancel', resumeAfterInteraction);
    stage.addEventListener('focusin', pauseForInteraction);
    stage.addEventListener('focusout', resumeAfterInteraction);

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting && entry.intersectionRatio > 0.05;
          syncRotation();
        });
      }, { rootMargin: '10% 0px', threshold: [0, 0.05, 0.35] });
      visibilityObserver.observe(stage);

      if (autoLoadEnabled) {
        autoLoadObserver = new IntersectionObserver(function (entries, observer) {
          entries.forEach(function (entry) {
            nearViewport = entry.isIntersecting;
            if (!nearViewport) return;
            activate(false);
            if (loaded || loading) observer.unobserve(entry.target);
          });
        }, { rootMargin: '320px 0px', threshold: 0.01 });
        autoLoadObserver.observe(stage);
      }
    } else if (autoLoadEnabled) {
      nearViewport = true;
      activate(false);
    }

    var controller = {
      sync: syncRotation,
      handleMotionPreference: function () {
        if (reduceMotion.matches) {
          userPaused = true;
          reducedMotionOverride = false;
        }
        syncRotation();
        if (!reduceMotion.matches && nearViewport && !saveDataEnabled()) activate(false);
      },
      handleDesktopPreference: function () {
        if (!nearViewport) return;
        if (!loaded && !loading && !reduceMotion.matches && !saveDataEnabled()) activate(false);
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        removeLoadListeners();
        if (visibilityObserver) visibilityObserver.disconnect();
        if (autoLoadObserver) autoLoadObserver.disconnect();
        loadButton.removeEventListener('click', handleLoadClick);
        if (rotationButton) rotationButton.removeEventListener('click', handleRotationClick);
        stage.removeEventListener('pointerdown', pauseForInteraction);
        stage.removeEventListener('pointerup', resumeAfterInteraction);
        stage.removeEventListener('pointercancel', resumeAfterInteraction);
        stage.removeEventListener('focusin', pauseForInteraction);
        stage.removeEventListener('focusout', resumeAfterInteraction);
        model.removeAttribute('auto-rotate');
        view.removeAttribute('data-tv8-model-ready');
        modelControllerByView.delete(view);
        modelControllers.delete(controller);
      }
    };

    modelControllerByView.set(view, controller);
    modelControllers.add(controller);
  }

  function initMarquee(root) {
    if (!root || root.dataset.tv8MarqueeReady === 'true') return;
    root.dataset.tv8MarqueeReady = 'true';

    function sync() {
      root.classList.toggle('is-paused', reduceMotion.matches || document.hidden);
    }

    var controller = {
      sync: sync,
      destroy: function () {
        root.removeAttribute('data-tv8-marquee-ready');
        marqueeControllerByRoot.delete(root);
        marqueeControllers.delete(controller);
      }
    };
    marqueeControllerByRoot.set(root, controller);
    marqueeControllers.add(controller);
    sync();
  }

  function initNavigation(details) {
    if (!details || details.dataset.tv8NavReady === 'true') return;
    details.dataset.tv8NavReady = 'true';

    var summary = details.querySelector('summary');
    var nav = details.querySelector('nav');
    var label = details.querySelector('.tv3-mobile-nav__label');
    if (!summary || !nav) return;

    var openLabel = details.dataset.navOpenLabel || 'Open navigation';
    var closeLabel = details.dataset.navCloseLabel || 'Close navigation';
    var openText = details.dataset.navOpenText || 'Close';
    var closedText = details.dataset.navClosedText || 'Menu';

    if (!nav.id) nav.id = 'Tv8MobileNavigation-' + Math.random().toString(36).slice(2, 9);
    summary.setAttribute('aria-controls', nav.id);

    function update() {
      var open = details.hasAttribute('open');
      summary.setAttribute('aria-expanded', String(open));
      summary.setAttribute('aria-label', open ? closeLabel : openLabel);
      if (label) label.textContent = open ? openText : closedText;
    }

    function close(returnFocus) {
      if (!details.hasAttribute('open')) return;
      details.removeAttribute('open');
      update();
      if (returnFocus) summary.focus();
    }

    function handleToggle() {
      if (details.open) {
        document.querySelectorAll('.tv3-mobile-nav[open]').forEach(function (other) {
          if (other === details) return;
          var otherController = navigationControllerByDetails.get(other);
          if (otherController) otherController.close(false);
          else other.removeAttribute('open');
        });
      }
      update();
    }

    function handleNavClick(event) {
      if (event.target.closest('a')) close(false);
    }

    function handleKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close(true);
    }

    function handleDocumentPointerdown(event) {
      if (!details.open || details.contains(event.target)) return;
      close(false);
    }

    details.addEventListener('toggle', handleToggle);
    nav.addEventListener('click', handleNavClick);
    details.addEventListener('keydown', handleKeydown);
    document.addEventListener('pointerdown', handleDocumentPointerdown);

    var controller = {
      close: close,
      update: update,
      destroy: function () {
        details.removeEventListener('toggle', handleToggle);
        nav.removeEventListener('click', handleNavClick);
        details.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('pointerdown', handleDocumentPointerdown);
        details.removeAttribute('data-tv8-nav-ready');
        navigationControllerByDetails.delete(details);
      }
    };

    navigationControllerByDetails.set(details, controller);
    update();
  }

  function init(context) {
    var scope = context || document;
    initReveal(scope);
    scope.querySelectorAll('[data-tv8-product-view]').forEach(initProductView);
    scope.querySelectorAll('[data-tv8-demo-proof]').forEach(initMarquee);
    scope.querySelectorAll('.tv3-mobile-nav').forEach(initNavigation);
  }

  function refreshMotionPreferences() {
    modelControllers.forEach(function (controller) {
      controller.handleMotionPreference();
    });
    marqueeControllers.forEach(function (controller) {
      controller.sync();
    });
    if (reduceMotion.matches) {
      document.querySelectorAll('[data-tv8-reveal]').forEach(function (item) {
        item.classList.add('is-visible');
      });
    }
  }

  function refreshDesktopPreference() {
    modelControllers.forEach(function (controller) {
      controller.handleDesktopPreference();
    });
    if (!desktop.matches) return;
    document.querySelectorAll('.tv3-mobile-nav[open]').forEach(function (details) {
      var controller = navigationControllerByDetails.get(details);
      if (controller) controller.close(false);
      else details.removeAttribute('open');
    });
  }

  document.addEventListener('visibilitychange', function () {
    modelControllers.forEach(function (controller) {
      controller.sync();
    });
    marqueeControllers.forEach(function (controller) {
      controller.sync();
    });
  });

  onMediaChange(reduceMotion, refreshMotionPreferences);
  onMediaChange(desktop, refreshDesktopPreference);
  if (connection && connection.addEventListener) {
    connection.addEventListener('change', refreshDesktopPreference);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-tv8-product-view]').forEach(function (view) {
      var controller = modelControllerByView.get(view);
      if (controller) controller.destroy();
    });
    event.target.querySelectorAll('[data-tv8-demo-proof]').forEach(function (root) {
      var controller = marqueeControllerByRoot.get(root);
      if (controller) controller.destroy();
    });
    event.target.querySelectorAll('.tv3-mobile-nav').forEach(function (details) {
      var controller = navigationControllerByDetails.get(details);
      if (controller) controller.destroy();
    });
    if (revealObserver) {
      event.target.querySelectorAll('[data-tv8-reveal]').forEach(function (item) {
        revealObserver.unobserve(item);
      });
    }
  });
})();
